import React, { useRef,  useState, useEffect } from 'react';
import Topbar from '../components/Topbar';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import { useRefresh } from '../utils/RefreshContext';

// ✅ Correct Import
import {
    getCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    toggleCategoryStatus,
    getTypesList
} from '../services/categoryService';

const Categories = () => {
    const { refreshKey } = useRefresh();
    const [categories, setCategories] = useState([]);
    const [types, setTypes] = useState([]);

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [formMode, setFormMode] = useState('create');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [formData, setFormData] = useState({ name: '', type_id: '', icon: null, status: 1 });
    const [imagePreview, setImagePreview] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [search, setSearch] = useState('');

    // Pagination States
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    const imgBaseUrl = process.env.REACT_APP_IMG_BASE_URL;

    // Status Helpers (Backend: 1 = Active, 2 = Inactive)
    const getStatusText = (status) => (status === 1 || status === '1') ? 'Active' : 'Inactive';
    const getStatusClass = (status) => (status === 1 || status === '1') ? 'active' : 'inactive';
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    // Load Categories from API
    const loadCategories = async () => {
        setIsLoading(true);
        try {
            const params = {
                page,
                limit,
                search: search || undefined
            };
            const response = await getCategories(params);
            
            // Extract data and meta from response
            // Based on user provided JSON: { result, message, data, meta }
            const data = response?.data || [];
            const meta = response?.meta || {};

            setCategories(data);
            setTotalPages(meta.totalPages || 1);
            setTotalRecords(meta.total || 0);
        } catch (error) {
            console.error('Failed to load categories:', error);
            setCategories([]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTypes = async () => {
        try {
            const response = await getTypesList();
            const data = response?.data || response;

            if (data && Array.isArray(data)) {
                setTypes(data);
            } else {
                setTypes([]);
            }
        } catch (error) {
            console.error('Failed to load category types:', error);
            setTypes([]);
        }
    };

    // Initial Load & Debounced Search
  const prevSearch = useRef(search);

  useEffect(() => {
      const searchChanged = prevSearch.current !== search;
      prevSearch.current = search;

      const timer = setTimeout(() => {
          if (searchChanged && page !== 1) {
              setPage(1);
          } else {
              loadCategories();
          }
      }, searchChanged ? 500 : 0);

      return () => clearTimeout(timer);
  }, [search, page, limit, refreshKey]);


    useEffect(() => {
        fetchTypes();
    }, []);

    const renderPagination = () => {
        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (page <= 4) {
                for (let i = 1; i <= 5; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (page >= totalPages - 3) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                pages.push(page - 1);
                pages.push(page);
                pages.push(page + 1);
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    const handleCreate = () => {
        setFormMode('create');
        setFormData({ name: '', type_id: '', icon: null, status: 1 });
        setImagePreview(null);
        setIsFormOpen(true);
    };

    const handleView = (category) => {
        setSelectedCategory(category);
        setIsViewModalOpen(true);
    };

    const handleEdit = (category) => {
        setFormMode('edit');
        setSelectedCategory(category);
        setFormData({
            name: category.name || '',
            type_id: category.type_id || '',
            icon: category.icon || null,
            status: (category.status === 1 || category.status === '1') ? 1 : 2
        });
        setImagePreview(category.icon ? `${imgBaseUrl}${category.icon}` : null);
        setIsFormOpen(true);
    };

    const handleDelete = (id) => {
        setItemToDelete(id);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        setIsDeleting(true);
        try {
            await deleteCategory(itemToDelete);
            toast.success("Category deleted successfully");
            setIsDeleteModalOpen(false);
            setItemToDelete(null);
            loadCategories();   // Refresh list
        } catch (error) {
            console.log('Failed to delete category : ', error);
            // toast.error("Failed to delete category");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleToggleStatus = async (id, currentStatus) => {
        const newStatus = (currentStatus === 1 || currentStatus === '1') ? 2 : 1;
        setIsSubmitting(true);
        try {
            await toggleCategoryStatus({ id, status: newStatus });
            toast.success(`Status changed to ${newStatus === 1 ? 'Active' : 'Inactive'}`);
            loadCategories();   // Refresh list
        } catch (error) {
            console.log('Failed to update status : ', error);
            // toast.error("Failed to update status");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({ ...formData, icon: file });
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (!formData.icon && formMode === 'create') {
            toast.error("Category image is required");
            return;
        }

        setIsSubmitting(true);
        try {
            const data = new FormData();
            data.append('name', formData.name);
            data.append('type_id', formData.type_id);
            data.append('status', formData.status);

            if (formData.icon instanceof File) {
                data.append('icon', formData.icon);
            }

            if (formMode === 'create') {
                await createCategory(data);
                toast.success("Category created successfully");
            } else {
                await updateCategory(selectedCategory?.id, data);
                toast.success("Category updated successfully");
            }

            setIsFormOpen(false);
            loadCategories();   // Refresh list
        } catch (error) {
            console.log('Failed to create or update category : ', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div style={{ background: '#F0F4F8', minHeight: '100vh' }}>
            <Topbar title="Categories" subtitle="Manage item categories for airport locations" />

            <div className="content">
                <div className="card" style={{ padding: '0px', overflow: 'hidden' }}>
                    <div className="toolbar" style={{ padding: '15px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div className="search-wrap" style={{ flex: 1, maxWidth: '400px' }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2">
                                <circle cx="11" cy="11" r="8" />
                                <path d="M21 21l-4.35-4.35" />
                            </svg>
                            <input
                                className="search-input"
                                placeholder="Search categories..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <button className="btn btn-primary" onClick={handleCreate}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            Create Category
                        </button>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table className="tbl">
                            <thead>
                                <tr>
                                    <th>Category Name</th>
                                    <th>Image</th>
                                    <th>Type</th>
                                    <th>Status</th>
                                    <th>Created At</th>
                                    <th style={{ textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {categories.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: 'var(--grey)' }}>
                                            No categories found
                                        </td>
                                    </tr>
                                ) : (
                                    categories.map((cat) => (
                                        <tr key={cat.id}>
                                            <td><div style={{ fontWeight: 600, color: 'var(--navy)' }}>{cat.name}</div></td>
                                            <td>
                                                {cat.icon ? (
                                                    <img
                                                        src={`${imgBaseUrl}${cat.icon}`}
                                                        alt={cat.name}
                                                        style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 4, background: '#f8fafc', padding: 2 }}
                                                    />
                                                ) : (
                                                    <span style={{ fontSize: 11, color: 'var(--grey)' }}>No Image</span>
                                                )}
                                            </td>
                                            <td>
                                                <span style={{ fontSize: 13, color: 'var(--grey)' }}>
                                                    {types.find(t => t.id === cat.type_id || t.id === Number(cat.type_id))?.name || 'N/A'}
                                                </span>
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge badge-${getStatusClass(cat.status)}`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleToggleStatus(cat.id, cat.status)}
                                                    title="Click to toggle status"
                                                >
                                                    {getStatusText(cat.status)}
                                                </span>
                                            </td>
                                            <td style={{ color: 'var(--grey)', fontSize: 12 }}>{formatDate(cat.createdAt)}</td>
                                            <td>
                                                <div className="action-btns" style={{ justifyContent: 'flex-end' }}>
                                                    <button className="action-btn" title="View" onClick={() => handleView(cat)}>
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8">
                                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                            <circle cx="12" cy="12" r="3" />
                                                        </svg>
                                                    </button>
                                                    <button className="action-btn" title="Edit" onClick={() => handleEdit(cat)}>
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8">
                                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2-2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                        </svg>
                                                    </button>
                                                    <button className="action-btn danger" title="Delete" onClick={() => handleDelete(cat.id)}>
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8">
                                                            <polyline points="3 6 5 6 21 6" />
                                                            <path d="M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    
                    {/* PAGINATION FOOTER */}
                    {categories.length > 0 && (
                        <div className="pagination-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 26px', borderTop: '1px solid #F0F4F8' }}>
                            <div style={{ fontSize: 13, color: 'var(--grey)' }}>
                                Showing <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{((page - 1) * limit) + 1}</span> to <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{Math.min(page * limit, totalRecords)}</span> of <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{totalRecords}</span> entries
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                    className="btn btn-outline"
                                    disabled={page === 1}
                                    onClick={() => setPage(page - 1)}
                                    style={{ padding: '6px 12px', fontSize: 12 }}
                                >
                                    Previous
                                </button>
                                <div style={{ display: 'flex', gap: 4 }}>
                                    {renderPagination().map((p, i) => (
                                        <button
                                            key={i}
                                            className={`btn ${page === p ? 'btn-primary' : 'btn-outline'}`}
                                            onClick={() => typeof p === 'number' && setPage(p)}
                                            style={{
                                                padding: '6px 12px',
                                                fontSize: 12,
                                                minWidth: 36,
                                                cursor: typeof p === 'number' ? 'pointer' : 'default',
                                                border: typeof p === 'number' ? '' : 'none',
                                                background: typeof p === 'number' ? '' : 'transparent'
                                            }}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    className="btn btn-outline"
                                    disabled={page === totalPages}
                                    onClick={() => setPage(page + 1)}
                                    style={{ padding: '6px 12px', fontSize: 12 }}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* VIEW MODAL */}
            {isViewModalOpen && selectedCategory && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(10, 25, 48, 0.4)', backdropFilter: 'blur(4px)',
                    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
                }}>
                    <div className="card" style={{ maxWidth: 500, padding: 0, width: '100%', animation: 'slideUp 0.3s ease-out' }}>
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3
                                style={{ margin: 0, fontSize: 18, color: 'var(--navy)', fontFamily: "'Space Grotesk', sans-serif" }}
                            >
                                Category Details
                            </h3>
                            <button
                                onClick={() => setIsViewModalOpen(false)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--grey)' }}
                            >✕</button>
                        </div>
                        <div style={{ padding: '24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginBottom: 24 }}>
                                <div style={{ width: 60, height: 60, borderRadius: 12, background: 'var(--light)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                    {selectedCategory.icon ? (
                                        <img
                                            src={`${imgBaseUrl}${selectedCategory.icon}`}
                                            alt={selectedCategory.name}
                                            style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }}
                                        />
                                    ) : (
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2">
                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                            <circle cx="8.5" cy="8.5" r="1.5" />
                                            <polyline points="21 15 16 10 5 21" />
                                        </svg>
                                    )}
                                </div>
                                <div>
                                    <h2 style={{ margin: 0, fontSize: 22, color: 'var(--navy)', fontFamily: "'Space Grotesk', sans-serif" }}>{selectedCategory.name}</h2>
                                    <span className={`badge badge-${getStatusClass(selectedCategory.status)}`} style={{ marginTop: 5 }}>
                                        {getStatusText(selectedCategory.status)}
                                    </span>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div className="field-row" style={{ border: 'none' }}>
                                    <div className="field-label">Category Image</div>
                                    <div className="field-value">{selectedCategory.icon ? 'Uploaded' : 'None'}</div>
                                </div>
                                <div className="field-row" style={{ border: 'none' }}>
                                    <div className="field-label">Category Type</div>
                                    <div className="field-value">
                                        {types.find(t => t.id === selectedCategory.type_id || t.id === Number(selectedCategory.type_id))?.name || 'N/A'}
                                    </div>
                                </div>
                                {/* <div className="field-row" style={{ border: 'none' }}>
                                    <div className="field-label">Linked Items</div>
                                    <div className="field-value">{selectedCategory.count}</div>
                                </div> */}
                                <div className="field-row" style={{ border: 'none' }}>
                                    <div className="field-label">Created At</div>
                                    <div className="field-value">{formatDate(selectedCategory.createdAt)}</div>
                                </div>
                                <div className="field-row" style={{ border: 'none' }}>
                                    <div className="field-label">Category ID</div>
                                    <div className="field-value">#{selectedCategory.id}</div>
                                </div>
                              
                            </div>

                            <div style={{ marginTop: 30, display: 'flex', gap: 12 }}>
                                <button className="btn btn-outline" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setIsViewModalOpen(false)}>Close</button>
                                <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={() => { setIsViewModalOpen(false); handleEdit(selectedCategory); }}>Edit Category</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* FORM MODAL */}
            {isFormOpen && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(10, 25, 48, 0.4)', backdropFilter: 'blur(4px)',
                    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
                }}>
                    <div className="card" style={{ maxWidth: 450, padding: 0, width: '100%', animation: 'slideUp 0.3s ease-out' }}>
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: 18, color: 'var(--navy)', fontFamily: "'Space Grotesk', sans-serif" }}>
                                {formMode === 'create' ? 'Create New Category' : 'Update Category'}
                            </h3>
                            <button onClick={() => setIsFormOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--grey)' }}>✕</button>
                        </div>
                        <form onSubmit={handleFormSubmit} style={{ padding: '24px' }}>
                            <div style={{ marginBottom: 20 }}>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>Category Name</label>
                                <input
                                    className="form-input"
                                    required
                                    placeholder="e.g. Food & Dining"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div style={{ marginBottom: 20 }}>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>
                                    Type <span style={{ color: '#EF4444' }}>*</span>
                                </label>
                                <select
                                    className="form-input"
                                    required
                                    value={formData.type_id}
                                    onChange={(e) => setFormData({ ...formData, type_id: e.target.value })}
                                    style={{ width: '100%' }}
                                >
                                    <option value="">Select Type</option>
                                    {types.map(type => (
                                        <option key={type.id} value={type.id}>{type.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ marginBottom: 20 }}>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>
                                    Category Image <span style={{ color: '#EF4444' }}>*</span>
                                </label>
                                <div style={{
                                    border: '2px dashed #E2E8F0',
                                    borderRadius: 12,
                                    padding: '20px',
                                    textAlign: 'center',
                                    background: '#F8FAFC',
                                    position: 'relative',
                                    cursor: 'pointer'
                                }}>
                                    {imagePreview ? (
                                        <div style={{ position: 'relative', display: 'inline-block' }}>
                                            <img
                                                src={imagePreview}
                                                alt="Preview"
                                                style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 8, display: 'block' }}
                                            />
                                            <div style={{
                                                position: 'absolute', top: -10, right: -10,
                                                background: '#EF4444', color: 'white',
                                                width: 24, height: 24, borderRadius: '50%',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: 12, cursor: 'pointer', border: '2px solid white'
                                            }} onClick={(e) => { e.stopPropagation(); setFormData({ ...formData, icon: null }); setImagePreview(null); }}>
                                                ✕
                                            </div>
                                        </div>
                                    ) : (
                                        <div onClick={() => document.getElementById('category-icon-upload').click()}>
                                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" style={{ marginBottom: 10 }}>
                                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                                <circle cx="8.5" cy="8.5" r="1.5" />
                                                <polyline points="21 15 16 10 5 21" />
                                            </svg>
                                            <div style={{ fontSize: 13, color: '#64748B' }}>Click to upload image</div>
                                            <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>PNG, JPG or SVG (Max. 2MB)</div>
                                        </div>
                                    )}
                                    <input
                                        id="category-icon-upload"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        style={{ display: 'none' }}
                                        required={formMode === 'create' && !formData.icon}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: 30 }}>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>Status</label>
                                <select
                                    className="form-input"
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: Number(e.target.value) })}
                                    style={{ width: '100%' }}
                                >
                                    <option value={1}>Active</option>
                                    <option value={2}>Inactive</option>
                                </select>
                            </div>

                            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                                <button type="button" className="btn btn-outline" onClick={() => setIsFormOpen(false)}>Cancel</button>
                                <button type="submit" className="btn btn-primary" style={{ minWidth: 120 }} disabled={isSubmitting}>
                                    {isSubmitting ? 'Saving...' : (formMode === 'create' ? 'Create Category' : 'Save Changes')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* DELETE CONFIRMATION MODAL */}
            {isDeleteModalOpen && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(10, 25, 48, 0.4)', backdropFilter: 'blur(4px)',
                    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
                }}>
                    <div className="card" style={{ maxWidth: 400, padding: 0, width: '100%', animation: 'slideUp 0.3s ease-out' }}>
                        <div style={{ padding: '30px 24px', textAlign: 'center' }}>
                            <div style={{
                                width: 56, height: 56, borderRadius: '50%', background: '#FEE2E2',
                                color: '#EF4444', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', margin: '0 auto 20px'
                            }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 6h18M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" />
                                </svg>
                            </div>
                            <h3 style={{ margin: '0 0 10px 0', fontSize: 18, color: 'var(--navy)', fontFamily: "'Space Grotesk', sans-serif" }}>Delete Category?</h3>
                            <p style={{ margin: 0, fontSize: 14, color: 'var(--grey)', lineHeight: 1.5 }}>
                                Are you sure you want to remove this category? This action will decouple it from all linked locations.
                            </p>
                        </div>
                        <div style={{ padding: '15px 24px', background: '#F8FAFC', borderTop: '1px solid #F1F5F9', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, display: 'flex', gap: 12 }}>
                            <button className="btn btn-outline" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setIsDeleteModalOpen(false)} disabled={isDeleting}>Cancel</button>
                            <button className="btn btn-danger" style={{ flex: 1, justifyContent: 'center', background: '#EF4444', color: 'white', border: 'none' }} onClick={confirmDelete} disabled={isDeleting}>
                                {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .content { padding: 24px; max-width: 1400px; margin: 0 auto; }
                .action-btns { display: flex; gap: 8px; }
                .badge-inactive { background: #FEE2E2; color: #991B1B; }
                .badge-active { background: #DCFCE7; color: #166534; }
                @media (max-width: 768px) {
                    .content { padding: 15px; }
                    .toolbar { flex-direction: column; gap: 12px; align-items: stretch !important; }
                    .search-wrap { maxWidth: 100% !important; }
                }
            `}</style>
            {(isLoading || isSubmitting || isDeleting) && <Spinner />}
        </div>
    );
};

export default Categories;